import React, { Component } from 'react';
import { getExecutionHistory } from '../../../services/monitoring.service';

/**
 * Execution History Dots Component
 * Displays last 5 execution statuses as colored dots
 * Fetches execution history from API on mount
 *
 * Props:
 * - serviceName: Name of the service to fetch history for
 *
 * Dots:
 * - Green dot: Success
 * - Red dot: Failed
 * - Gray dot: No data
 *
 * Order: Most recent execution on the right
 */
class ExecutionHistoryDots extends Component {
  constructor(props) {
    super(props);
    this.state = {
      history: [],
      loading: false,
      error: null,
    };
    this.isFetching = false; // Track fetch state separately
  }

  componentDidMount() {
    //console.log('[ExecutionHistoryDots] Mounting for service:', this.props.serviceName);
    this.fetchHistory();
  }

  componentDidUpdate(prevProps) {
    // Refetch if service name changes
    if (prevProps.serviceName !== this.props.serviceName) {
      //console.log('[ExecutionHistoryDots] Service name changed from', prevProps.serviceName, 'to', this.props.serviceName);
      this.fetchHistory();
    }
  }

  componentWillUnmount() {
    //console.log('[ExecutionHistoryDots] Unmounting for service:', this.props.serviceName);
    this.isFetching = false; // Cancel any in-flight requests
  }

  fetchHistory = async () => {
    const { serviceName } = this.props;

    // Prevent duplicate calls for the same service while fetching
    if (this.isFetching) {
      //console.log('[ExecutionHistoryDots] Skipping fetch - already fetching for:', serviceName);
      return;
    }

    try {
      //console.log('[ExecutionHistoryDots] Fetching history for:', serviceName);
      this.isFetching = true;
      this.setState({ loading: true, error: null });
      const response = await getExecutionHistory(serviceName, 5);
      const history = response.Data || response.data || response;
      this.setState({ history, loading: false });
    } catch (error) {
      console.error('[ExecutionHistoryDots] Error fetching execution history for', serviceName, ':', error);
      this.setState({ error: error.message, loading: false, history: [] });
    } finally {
      this.isFetching = false;
    }
  };

  renderDot = (execution, index) => {
    if (!execution) {
      // No data - gray dot
      return (
        <span
          key={`empty-${index}`}
          className="execution-dot"
          style={{
            backgroundColor: '#dee2e6',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            display: 'inline-block',
            marginRight: '4px',
          }}
          title="No data"
        ></span>
      );
    }

    const isSuccess = execution.status === 'Success';
    const color = isSuccess ? '#28a745' : '#dc3545'; // Green or Red
    const title = `${execution.executionTime}\n${execution.status}${
      execution.errorMessage ? `\n${execution.errorMessage}` : ''
    }`;

    return (
      <span
        key={execution.executionId || index}
        className="execution-dot"
        style={{
          backgroundColor: color,
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          display: 'inline-block',
          marginRight: '4px',
          cursor: 'help',
        }}
        title={title}
      ></span>
    );
  };

  render() {
    const { history, loading, error } = this.state;

    if (loading) {
      return (
        <div className="execution-history-dots">
          <span className="text-muted">
            <i className="fa fa-spinner fa-spin"></i>
          </span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="execution-history-dots">
          <span className="text-danger" title={error}>
            <i className="fa fa-exclamation-triangle"></i>
          </span>
        </div>
      );
    }

    // Ensure we always show 5 dots (fill with empty if needed)
    const dots = [];
    for (let i = 0; i < 5; i++) {
      dots.push(history[i] || null);
    }

    // Reverse to show oldest on left, newest on right
    const reversedDots = [...dots].reverse();

    return (
      <div className="execution-history-dots d-flex align-items-center">
        {reversedDots.map((execution, index) => this.renderDot(execution, index))}
      </div>
    );
  }
}

export default ExecutionHistoryDots;
